import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';

import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  pluck,
  startWith,
  switchMap,
  switchMapTo,
  throwError,
} from 'rxjs';

import { finShare } from '@fcom/rx';
import { mergeDeep } from '@fcom/core/utils';

import { SentryLogger } from '../sentry/sentry.logger';
import { mapErrorForSentry } from '../../utils';
import { CmsDataService, I18nData, I18nKey, i18nKeyToDataMap } from '../cms/cms-data.service';
import { LocalizationProvider } from './localization.provider';

@Injectable()
export class CmsLocalizationProvider extends LocalizationProvider {
  private cmsDataService = inject(CmsDataService);
  private sentryLogger = inject(SentryLogger);
  private router = inject(Router);
  private location = inject(Location);

  private lazyLocalizationsObj: Partial<Record<I18nKey, boolean>> = {};
  private lazyLocalizationsSubject = new BehaviorSubject<Partial<Record<I18nKey, boolean>>>(this.lazyLocalizationsObj);
  private lazyLocalizations$ = this.lazyLocalizationsSubject.asObservable();

  triggerLazyLocalizations(key: I18nKey): void {
    this.lazyLocalizationsObj = {
      ...this.lazyLocalizationsObj,
      [key]: true,
    };
    this.lazyLocalizationsSubject.next(this.lazyLocalizationsObj);
  }

  getLocalizations(lang$: Observable<string>): Observable<Record<string, unknown>> {
    return combineLatest([
      super.getLocalizations(lang$), // local localization file
      this.immediateLocalizations(lang$, i18nKeyToDataMap.global),
      this.immediateLocalizations(lang$, i18nKeyToDataMap.booking),
      this.lazyLoadLocalizations(lang$, i18nKeyToDataMap.taxCodes, ['localSettings', 'taxCodes']),
      this.lazyLoadLocalizations(lang$, i18nKeyToDataMap.countryRegions, ['localSettings', 'countryRegions']),
      this.lazyLoadLocalizations(lang$, i18nKeyToDataMap.mmb),
      this.lazyLoadLocalizations(lang$, i18nKeyToDataMap.reaccommodation),
      this.lazyLoadLocalizations(lang$, i18nKeyToDataMap.loyalty),
      this.lazyLoadLocalizations(lang$, i18nKeyToDataMap.serviceForms),
      this.lazyLoadLocalizations(lang$, i18nKeyToDataMap.corporate),
      this.lazyLoadLocalizations(lang$, i18nKeyToDataMap.refunds),
      this.lazyLoadLocalizations(lang$, i18nKeyToDataMap.travelReady),
    ]).pipe(
      map(
        ([
          booking,
          cms,
          base,
          taxCodes,
          countryRegions,
          mmb,
          reaccommodation,
          loyalty,
          serviceForms,
          corporate,
          refunds,
          travelReady,
        ]) =>
          [
            booking,
            cms,
            taxCodes,
            countryRegions,
            mmb,
            reaccommodation,
            loyalty,
            serviceForms,
            corporate,
            refunds,
            travelReady,
          ].reduce((combo, current) => mergeDeep(combo, current), base)
      ),
      finShare()
    );
  }

  private immediateLocalizations(lang$: Observable<string>, i18nData: I18nData): Observable<Record<string, unknown>> {
    return lang$.pipe(
      switchMap((l) => this.cmsDataService.getLocalization(l, i18nData)),
      catchError(this.throwCatchError(`Error fetching ${i18nData.key} localization from CMS`))
    );
  }

  private lazyLoadLocalizations(
    lang$: Observable<string>,
    i18nData: I18nData,
    pluckProperty: string[] = ['localSettings']
  ): Observable<Record<string, unknown>> {
    return this.lazyLocalizations$.pipe(
      pluck(i18nData.key),
      filter(Boolean),
      distinctUntilChanged(),
      switchMapTo(lang$),
      switchMap((language) => this.cmsDataService.getLocalization(language, i18nData, pluckProperty)),
      catchError(this.throwCatchError(`Error fetching ${i18nData.key} localization from CMS`, true)),
      startWith({})
    );
  }

  private throwCatchError(
    errorMsg: string,
    canHandleError = false
  ): (error: unknown) => Observable<Record<string, unknown>> {
    return (error) => {
      this.sentryLogger.error(errorMsg, { error: mapErrorForSentry(error) });
      if (canHandleError) {
        return of({});
      } else {
        const [, lang] = this.location.path(false).split('/');
        this.router.navigate([lang || 'en', 'sorry', 'LOCALIZATION_ERROR']);
        return throwError(() => errorMsg);
      }
    };
  }
}
